// The articulated rabbit drawn by HeroRabbit.vue: its static shapes, the
// named poses and the solver that turns a pose into the transform of every
// body part.
//
// Rig units: the rabbit is ~100 units from tail to nose. The origin is on the
// ground under its body, +x is the way it faces and +y points down (SVG).
// Angles are in degrees, clockwise, 0 = forward, 90 = down, 180 = backward.
// Leg angles in a pose are relative to the torso so a tilting body carries its
// legs along; planted legs are solved with IK so their feet stay on the ground.

import { lerp, clamp } from './heroMotion';

const DEG = Math.PI / 180;

// Bone lengths; `c` runs from the last joint to the centre of the toe tip,
// `r` is the toe tip radius (how high its centre sits above the ground) and
// `bend` the side the middle joint folds to (+1 knee forward, -1 elbow back).
export const LIMB = {
  hind: { a: 18, b: 22, c: 27, r: 2.5, bend: 1 },
  front: { a: 13, b: 12, c: 7.5, r: 2.3, bend: -1 }
};

// The head is drawn a touch large; HeroRabbit.vue scales it by this.
export const HEAD_SCALE = 0.94;

/* ---------------------------------------------------------------------- */
/* Path helpers                                                           */
/* ---------------------------------------------------------------------- */

function n2(v) {
  return Math.round(v * 100) / 100;
}

// Closed Catmull-Rom spline through the points, as cubic Beziers.
export function smoothClosed(pts) {
  const n = pts.length;
  let d = 'M' + n2(pts[0][0]) + ',' + n2(pts[0][1]);
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    d += 'C' + n2(p1[0] + (p2[0] - p0[0]) / 6) + ',' + n2(p1[1] + (p2[1] - p0[1]) / 6) +
      ' ' + n2(p2[0] - (p3[0] - p1[0]) / 6) + ',' + n2(p2[1] - (p3[1] - p1[1]) / 6) +
      ' ' + n2(p2[0]) + ',' + n2(p2[1]);
  }
  return d + 'Z';
}

// A tapered limb segment along +x: a circle of radius r0 at x0 joined by its
// outer tangents to a circle of radius r1 at x0 + len.
export function capsule(x0, len, r0, r1) {
  const sp = clamp((r0 - r1) / len, -0.95, 0.95);
  const cp = Math.sqrt(1 - sp * sp);
  const x1 = x0 + len;
  const p0 = n2(x0 + r0 * sp) + ',' + n2(-r0 * cp);
  const p1 = n2(x1 + r1 * sp) + ',' + n2(-r1 * cp);
  const q1 = n2(x1 + r1 * sp) + ',' + n2(r1 * cp);
  const q0 = n2(x0 + r0 * sp) + ',' + n2(r0 * cp);
  const big0 = r0 >= r1 ? 1 : 0;
  return 'M' + p0 + 'L' + p1 + 'A' + r1 + ',' + r1 + ' 0 ' + (1 - big0) + ' 1 ' + q1 +
    'L' + q0 + 'A' + r0 + ',' + r0 + ' 0 ' + big0 + ' 1 ' + p0 + 'Z';
}

// A single grass blade rising from (x, y).
function blade(x, y, h, lean, w) {
  const hw = w / 2;
  return 'M' + n2(x - hw) + ',' + n2(y) +
    'Q' + n2(x - hw * 0.4 + lean * 0.35) + ',' + n2(y - h * 0.55) + ' ' + n2(x + lean) + ',' + n2(y - h) +
    'Q' + n2(x + hw * 0.5 + lean * 0.3) + ',' + n2(y - h * 0.5) + ' ' + n2(x + hw) + ',' + n2(y) + 'Z';
}

function blades(list) {
  return list.map((b) => blade(b[0], b[1], b[2], b[3], b[4] || 2.2)).join('');
}

// The cottontail: one soft round tuft with a slightly ragged outline (short
// alternating radii), so it reads as fur rather than a stack of balls.
function tuft(cx, cy, rx, ry, n, jag, seed) {
  const pts = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + seed;
    const w = 1 + (i % 2 ? -jag : jag * 0.6) + 0.04 * Math.sin(i * 2.7 + seed * 3);
    pts.push([cx + Math.cos(a) * rx * w, cy + Math.sin(a) * ry * w]);
  }
  return smoothClosed(pts);
}

/* ---------------------------------------------------------------------- */
/* Shapes                                                                 */
/* ---------------------------------------------------------------------- */

// Torso outline around its centre, plus the stretched (airborne) and the
// compressed (crouch / landing) versions it morphs toward.
const TORSO = {
  base: [[-35, 1], [-32, -14], [-20, -24.5], [-4, -22], [11, -16.5], [22, -9], [27, 3], [20, 14.5], [0, 19], [-24, 16.5]],
  long: [[-43, 1], [-38, -11], [-20, -19], [0, -17], [17, -13.5], [30, -6], [34, 4], [25, 12.5], [0, 15], [-30, 13]],
  short: [[-30, 3], [-29, -15], [-17, -27], [-2, -25], [11, -18], [20, -8.5], [23, 4.5], [16, 16], [-1, 21], [-22, 17.5]]
};

const torsoBuf = TORSO.base.map((p) => [p[0], p[1]]);

// st: -1 compressed .. 0 relaxed .. +1 stretched.
export function torsoPoints(st) {
  const key = st >= 0 ? TORSO.long : TORSO.short;
  const w = Math.min(Math.abs(st), 1.2);
  for (let i = 0; i < torsoBuf.length; i++) {
    torsoBuf[i][0] = lerp(TORSO.base[i][0], key[i][0], w);
    torsoBuf[i][1] = lerp(TORSO.base[i][1], key[i][1], w);
  }
  return torsoBuf;
}

export function torsoPath(st) {
  return smoothClosed(torsoPoints(st));
}

// Where the limbs, head and tail attach, in torso coordinates.
function attachments(st, out) {
  out.hipX = -14 - 5 * st;
  out.hipY = -1;
  out.shX = 16 + 5 * st;
  out.shY = 4;
  out.neckX = 22 + 7 * st;
  out.neckY = -10 + 1.5 * st;
  out.tailX = -33 - 8 * st;
  out.tailY = -7 + 2 * st;
}

// Far-side limbs attach slightly up and forward (a hint of perspective).
const FAR_HIP = [3, -2];
const FAR_SHOULDER = [3.5, -1.5];

const HEAD = [[-7, -2], [-5, -14], [5, -23], [17, -22.5], [26, -15.5], [31.5, -9], [30.5, -3], [24, 1.5], [12, 4], [0, 4]];

export const SHAPES = {
  head: smoothClosed(HEAD),
  thigh: smoothClosed([[-12, 1], [-8.5, -11], [4, -15.5], [16, -10.5], [23, -1.5], [19, 7.5], [7, 11], [-5.5, 9.5]]),
  shin: capsule(0, LIMB.hind.b, 4.4, 2.8),
  foot: capsule(-3.5, LIMB.hind.c + 3.5, 3.3, LIMB.hind.r),
  upperArm: capsule(0, LIMB.front.a, 4.4, 3.0),
  forearm: capsule(0, LIMB.front.b, 3.0, 2.4),
  paw: capsule(-1, LIMB.front.c + 1, 2.6, LIMB.front.r),
  tail: tuft(-3.2, -2.2, 6.2, 5.6, 12, 0.03, 0.3),
  tailHi: tuft(-4.4, -4.2, 3.4, 2.7, 10, 0.07, 1.1),
  // Ear pointing up from its base; the inner ear sits toward the front edge.
  ear: 'M-4.6,0C-6.6,-9 -6.9,-22 -4.5,-30C-3.1,-35.2 1.3,-36.2 2.9,-32.4C5.5,-25 5.9,-11 4.4,0Z',
  earInner: 'M-1.9,-2.6C-3.5,-10 -3.6,-21 -2.1,-27.8C-1.3,-31 0.6,-31.4 1.5,-28.9C3.2,-22 3.6,-11 2.8,-2.6Z',
  earTip: 'M-4.4,-30.4C-3.1,-35.2 1.3,-36.2 2.9,-32.4C1.2,-33.7 -2,-33.5 -4.4,-30.4Z',
  earEdge: 'M-4.6,0C-6.6,-9 -6.9,-22 -4.5,-30C-5.4,-22 -5.2,-9 -3.2,0Z',
  earRim: 'M-5.2,-6C-6.6,-14 -6.5,-24 -4.3,-30.6C-3,-34.8 0.6,-35.8 2.4,-33.4',
  nose: 'M29.7,-9.5C30.5,-10.5 32.3,-10.3 32.7,-9.1C32.8,-8.1 32,-7.3 31.2,-7.2C30.4,-7.5 29.6,-8.4 29.7,-9.5Z',
  mouth: 'M31.1,-7.2L30.8,-5.4M30.8,-5.4C30.1,-4.3 28.9,-4.2 28.1,-4.7',
  whiskers: 'M28.4,-6.2C32.8,-8.6 36.8,-9.6 40.8,-9.8M28.7,-5.3C33.3,-5.8 37.3,-5.4 41.3,-4.4M28.5,-4.6C32.3,-3.6 35.3,-2.2 38.3,-0.3',
  // The closed eye: the lid line curving down across the eye.
  eyeShut: 'M10.3,-12.6C12.4,-13.3 16.6,-13.3 18.8,-12.6',
  // Burrow (its own units, scaled like the rabbit; origin = hole centre).
  mound: 'M-62,3C-51,-4.5 -35,-17.5 -6,-19C20,-20 44,-9.5 64,3Z',
  earth: 'M-32,1.6C-32,-10.5 -17.5,-18 0,-18C17.5,-18 32,-10.5 33,1.6C24.5,5.2 -23.5,5.2 -32,1.6Z',
  // The mouth: an arch, with the tunnel floor sloping away into the dark.
  hole: 'M-22.5,1.6C-23,-9.5 -12.8,-15.4 0,-15.4C12.8,-15.4 23,-9.5 22.5,1.6C13.5,4.1 -13.5,4.1 -22.5,1.6Z',
  holeFloor: 'M-18,2C-11,-3.2 11,-3.2 18,2C11,3.6 -11,3.6 -18,2Z',
  // Turf hanging over the top of the earth collar.
  overhang: 'M-31.5,-6.5 L-31.2,-9.0 L-30.4,-10.8 L-29.1,-12.4 L-27.3,-13.9 L-25.0,-15.1 L-22.3,-16.2 L-19.2,-17.1 L-15.8,-17.9 L-12.1,-18.5 L-8.2,-18.9 L-4.1,-19.2 L0.0,-19.3 L4.1,-19.2 L8.2,-18.9 L12.1,-18.5 L15.7,-17.9 L19.2,-17.1 L22.3,-16.2 L25.0,-15.1 L27.3,-13.9 L29.1,-12.4 L30.4,-10.8 L31.2,-9.0 L31.5,-6.5 L29.5,-4.6 Q28.9,-1.5 28.3,-8.9 Q26.6,-7.1 24.8,-11.8 Q22.1,-6.9 19.3,-14.0 Q15.8,-10.8 12.3,-15.5 Q8.2,-10.3 4.2,-16.3 Q0.0,-13.5 -4.2,-16.3 Q-8.2,-11.1 -12.3,-15.5 Q-15.8,-8.6 -19.3,-14.0 Q-22.1,-9.3 -24.8,-11.8 Q-26.6,-4.9 -28.3,-8.9 Q-28.9,-2.3 -29.5,-4.6Z',
  lip: 'M-27.5,1.3C-16,6.2 16,6.2 27.5,1.3C16,3.7 -16,3.7 -27.5,1.3Z',
  bladesBack: blades([[-50, 2, 7, -2.2], [-45, 0.5, 10, 0.8], [-39, -1.5, 6.5, -1.2], [36, -1.5, 7.5, 1.4], [42, 0.2, 10.5, 2.6], [50, 1.6, 6.5, 0.6]]),
  bladesBackHi: blades([[-47.5, 1.2, 8, 2.2, 1.8], [39, -0.8, 8.5, -1.4, 1.8], [53, 2.4, 7, 2.2, 1.8]]),
  bladesFront: blades([[-28, 2.8, 6.5, -1.8], [-24.5, 3.7, 8.5, 0.9], [24, 3.7, 7.5, 1.8], [28, 2.6, 5.5, -0.8]]),
  bladesFrontHi: blades([[-26.3, 3.4, 5.5, 1.6, 1.6], [26, 3.2, 6.5, -1.2, 1.6]])
};

/* ---------------------------------------------------------------------- */
/* Poses                                                                  */
/* ---------------------------------------------------------------------- */

const LEGS = ['hN', 'hF', 'fN', 'fF'];
const LEG_FIELDS = ['p', 'x', 'y', 'fa', 'a1', 'a2', 'a3'];
const BASE_FIELDS = ['cx', 'cy', 'cr', 'st', 'hr', 'ht', 'nx', 'ny', 'eN', 'eF', 'eNs', 'eFs', 'tr', 'ts'];

function legKeys(legs) {
  return legs.reduce((acc, leg) => acc.concat(LEG_FIELDS.map((f) => leg + f)), []);
}

export const KEYS = BASE_FIELDS.concat(legKeys(LEGS));
// The hind legs alone, for blends that time them separately from the body.
export const HIND_KEYS = legKeys(['hN', 'hF']);

export function newPose() {
  const o = {};
  for (let i = 0; i < KEYS.length; i++) o[KEYS[i]] = 0;
  o.eNs = 1;
  o.eFs = 1;
  o.ts = 1;
  return o;
}

export function copyPose(out, a) {
  for (let i = 0; i < KEYS.length; i++) out[KEYS[i]] = a[KEYS[i]];
  return out;
}

export function blendInto(out, a, b, w) {
  return blendKeys(out, KEYS, a, b, w);
}

export function blendKeys(out, keys, a, b, w) {
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    out[k] = a[k] + (b[k] - a[k]) * w;
  }
  return out;
}

// Pose sheet. Legs are either planted ({ x, fa }: toe position relative to
// the rig origin and world angle of the foot) or free ({ a: [..] }: angles
// relative to the torso). Missing values are taken from `sit`.
const RAW = {
  sit: {
    cx: -8, cy: -22, cr: 0, st: 0, hr: 0, ht: 0, nx: 0, ny: 0,
    eN: -16, eF: -30, eNs: 1, eFs: 1, tr: 4, ts: 1,
    hN: { x: 5, fa: 0 }, hF: { x: 8.5, fa: 0 }, fN: { x: 27, fa: 0 }, fF: { x: 31, fa: 0 }
  },
  // Anticipation before a hop: low, compressed, ears and head drop a little.
  crouch: {
    cx: -10, cy: -18.5, cr: 4, st: -0.45, hr: 9, eN: -30, eF: -42, tr: -2
  },
  // Last instant of the push-off: up on its toes with the hind legs almost
  // straight behind it, body long and nose-up, front paws reaching forward.
  // The hops pin the toes where they stood, so the push travels 5 - hN.x.
  launch: {
    cx: 2, cy: -41, cr: -21, st: 0.6, hr: -2, eN: -42, eF: -52, eNs: 0.97, tr: 28, ts: 1.04,
    hN: { x: -51, fa: 132 }, hF: { x: -47, fa: 126 },
    fN: { a: [40, 10, 0] }, fF: { a: [50, 18, 6] }
  },
  // The same moment when jumping out of the burrow (hind legs in the tunnel):
  // more upright, so the chest clears the turf before it passes the rim.
  launchOut: {
    cx: 2, cy: -40, cr: -34, st: 0.5, hr: 14, eN: -36, eF: -46, eNs: 0.97, tr: 22, ts: 1.04,
    hN: { a: [124, 146, 146] }, hF: { a: [118, 142, 142] },
    fN: { a: [44, 12, 2] }, fF: { a: [54, 20, 8] }
  },
  // Apex: fully stretched, hind legs trailing, ears swept back by the speed.
  air: {
    cx: 0, cy: -30, cr: -3, st: 0.62, hr: -4, eN: -52, eF: -58, eNs: 0.95, tr: 16, ts: 1.03,
    hN: { a: [150, 160, 170] }, hF: { a: [144, 154, 164] },
    fN: { a: [58, 34, 26] }, fF: { a: [66, 42, 32] }
  },
  // Mid-flight: the hind legs swing forward folded, knees forward and hocks
  // up behind, so the feet stay well clear of the ground.
  tuck: {
    cx: 1, cy: -31, cr: 4, st: 0.5, hr: -8, eN: -46, eF: -54, tr: 12,
    hN: { a: [62, 204, 62] }, hF: { a: [56, 198, 56] },
    fN: { a: [50, 22, 14] }, fF: { a: [58, 30, 20] }
  },
  // Coming down: nose-down, front legs reaching for the ground, the hind legs
  // already folded forward under the belly with the feet level, clear of
  // the ground until the front paws have touched down.
  reach: {
    cx: 2, cy: -29, cr: 14, st: 0.25, hr: -14, eN: -34, eF: -44, tr: 8,
    hN: { a: [12, 146, -8] }, hF: { a: [8, 142, -10] },
    fN: { x: 42, fa: 8 }, fF: { x: 46, fa: 8 }
  },
  // Front paws down, hind feet touching down; ears still swept back.
  land: {
    cx: -1, cy: -23, cr: 11, st: -0.15, hr: -9, eN: -10, eF: -18, eNs: 0.97, tr: -10,
    hN: { x: 9, fa: 4 }, hF: { x: 12, fa: 4 },
    fN: { x: 17, fa: 0 }, fF: { x: 21, fa: 0 }
  },
  // Squashed over its feet after a landing (also the start of the next hop).
  gather: {
    cx: -3, cy: -21.5, cr: 4, st: -0.5, hr: 5, eN: -14, eF: -26, tr: 0,
    hN: { x: 9, fa: 0 }, hF: { x: 12, fa: 0 }, fN: { x: 17, fa: 0 }, fF: { x: 21, fa: 0 }
  },
  // Standing up on the hind legs to look around.
  alert: {
    cx: -11, cy: -46, cr: -66, st: 0.12, hr: 60, ny: -1, eN: 0, eF: -12, tr: -6,
    fN: { a: [128, 80, 120] }, fF: { a: [134, 86, 126] }
  },
  // Upright in the burrow, paws tucked against the chest.
  peek: {
    cx: -11, cy: -46, cr: -66, st: 0.12, hr: 62, ny: -1, eN: -2, eF: -14, tr: -6,
    fN: { a: [128, 80, 120] }, fF: { a: [134, 86, 126] }
  },
  // Head down in the grass.
  nibble: {
    cx: -6, cy: -20, cr: 11, st: -0.15, hr: 42, eN: -36, eF: -48, tr: 2,
    fN: { x: 21, fa: 0 }, fF: { x: 25, fa: 0 }
  },
  // Head turned toward the viewer.
  look: {
    hr: 4, ht: 1, eN: -8, eF: -34
  },
  // Washing its face: sat up a little, paws raised to the muzzle.
  groom: {
    cx: -9, cy: -29, cr: -30, st: 0.04, hr: 53, eN: -22, eF: -34, tr: -4,
    fN: { a: [22, -34, -60] }, fF: { a: [28, -26, -52] }
  },
  // Diving nose-first into the burrow, ears flat along the back.
  dive: {
    cx: 6, cy: -26, cr: 56, st: 0.4, hr: 26, eN: -106, eF: -110, eNs: 0.92, tr: 20,
    hN: { a: [158, 166, 172] }, hF: { a: [152, 160, 166] },
    fN: { a: [74, 46, 40] }, fF: { a: [80, 52, 46] }
  }
};

/* ---------------------------------------------------------------------- */
/* Solver                                                                 */
/* ---------------------------------------------------------------------- */

// Angles relative to the torso are kept in [-90, 270) so blends between
// poses never take the long way round.
function wrap(a) {
  return ((a + 90) % 360 + 360) % 360 - 90;
}

const ikOut = [0, 0, 0];

// Two-bone IK to the joint above the foot, the foot itself at world angle fa
// with its toe tip resting at (tipX, tipY).
function ikLeg(L, rx, ry, tipX, tipY, fa, cr) {
  const far = fa * DEG;
  const hx = tipX - L.c * Math.cos(far);
  const hy = tipY - L.c * Math.sin(far);
  const d = clamp(Math.hypot(hx - rx, hy - ry), Math.abs(L.a - L.b) + 0.5, L.a + L.b - 0.05);
  const th = Math.atan2(hy - ry, hx - rx);
  const al = Math.acos(clamp((L.a * L.a + d * d - L.b * L.b) / (2 * L.a * d), -1, 1));
  const w1 = th - L.bend * al;
  const kx = rx + L.a * Math.cos(w1);
  const ky = ry + L.a * Math.sin(w1);
  const w2 = Math.atan2(hy - ky, hx - kx);
  ikOut[0] = wrap(w1 / DEG - cr);
  ikOut[1] = wrap(w2 / DEG - cr);
  ikOut[2] = wrap(fa - cr);
  return ikOut;
}

// Forward kinematics from world angles.
// out: [rootX, rootY, angle1, j1X, j1Y, angle2, j2X, j2Y, angle3, tipX, tipY]
function chain(L, rx, ry, w1, w2, w3, out) {
  const kx = rx + L.a * Math.cos(w1 * DEG);
  const ky = ry + L.a * Math.sin(w1 * DEG);
  const hx = kx + L.b * Math.cos(w2 * DEG);
  const hy = ky + L.b * Math.sin(w2 * DEG);
  out[0] = rx; out[1] = ry; out[2] = w1;
  out[3] = kx; out[4] = ky; out[5] = w2;
  out[6] = hx; out[7] = hy; out[8] = w3;
  out[9] = hx + L.c * Math.cos(w3 * DEG);
  out[10] = hy + L.c * Math.sin(w3 * DEG);
  return out;
}

// `floor` (rig y the toe tip centre may not pass, or null) keeps blended and
// free legs from poking through the ground: the leg is re-solved to rest
// its toe on the ground, keeping the angle of the foot.
function solveLeg(L, rx, ry, P, key, cr, floor, out) {
  let a1 = P[key + 'a1'];
  let a2 = P[key + 'a2'];
  let a3 = P[key + 'a3'];
  const pw = P[key + 'p'];
  if (pw > 0.001) {
    const ik = ikLeg(L, rx, ry, P[key + 'x'], -L.r - P[key + 'y'], P[key + 'fa'], cr);
    a1 = lerp(a1, ik[0], pw);
    a2 = lerp(a2, ik[1], pw);
    a3 = lerp(a3, ik[2], pw);
  }
  chain(L, rx, ry, cr + a1, cr + a2, cr + a3, out);
  if (floor !== null && out[10] > floor) {
    const ik = ikLeg(L, rx, ry, out[9], floor, cr + a3, cr);
    chain(L, rx, ry, cr + ik[0], cr + ik[1], cr + a3, out);
  }
  return out;
}

const att = {};

export function newSolved() {
  return {
    torso: [0, 0, 0],
    head: [0, 0, 0],
    tail: [0, 0, 0],
    hN: new Array(11).fill(0),
    hF: new Array(11).fill(0),
    fN: new Array(11).fill(0),
    fF: new Array(11).fill(0)
  };
}

// Pose -> transform of every part (rig units). With `rootY` (the vertical
// offset the rig is drawn at) the feet are kept above the ground; pass null
// while the rabbit is inside its burrow.
export function solvePose(P, R, rootY) {
  const cr = P.cr;
  const c = Math.cos(cr * DEG);
  const s = Math.sin(cr * DEG);
  attachments(P.st, att);
  const tx = (lx, ly) => P.cx + lx * c - ly * s;
  const ty = (lx, ly) => P.cy + lx * s + ly * c;
  const ground = typeof rootY === 'number';
  const hFloor = ground ? -LIMB.hind.r - rootY : null;
  const fFloor = ground ? -LIMB.front.r - rootY : null;

  R.torso[0] = P.cx; R.torso[1] = P.cy; R.torso[2] = cr;

  solveLeg(LIMB.hind, tx(att.hipX, att.hipY), ty(att.hipX, att.hipY), P, 'hN', cr, hFloor, R.hN);
  const fhx = att.hipX + FAR_HIP[0];
  const fhy = att.hipY + FAR_HIP[1];
  solveLeg(LIMB.hind, tx(fhx, fhy), ty(fhx, fhy), P, 'hF', cr, hFloor, R.hF);
  solveLeg(LIMB.front, tx(att.shX, att.shY), ty(att.shX, att.shY), P, 'fN', cr, fFloor, R.fN);
  const fsx = att.shX + FAR_SHOULDER[0];
  const fsy = att.shY + FAR_SHOULDER[1];
  solveLeg(LIMB.front, tx(fsx, fsy), ty(fsx, fsy), P, 'fF', cr, fFloor, R.fF);

  const nx = att.neckX + P.nx;
  const ny = att.neckY + P.ny;
  R.head[0] = tx(nx, ny); R.head[1] = ty(nx, ny); R.head[2] = cr + P.hr;
  R.tail[0] = tx(att.tailX, att.tailY); R.tail[1] = ty(att.tailX, att.tailY); R.tail[2] = cr + P.tr;
  return R;
}

// Expand the pose sheet. Planted legs also get the angles of their own IK
// solution and free legs the position of their toe, so blending a planted
// pose with a free one interpolates between two consistent states.
function buildPoses() {
  const out = {};
  const sit = RAW.sit;
  const solved = newSolved();
  Object.keys(RAW).forEach((name) => {
    const raw = RAW[name];
    const P = newPose();
    BASE_FIELDS.forEach((f) => {
      P[f] = raw[f] !== undefined ? raw[f] : sit[f];
    });
    LEGS.forEach((leg) => {
      const spec = raw[leg] || sit[leg];
      if (spec.a) {
        P[leg + 'p'] = 0;
        P[leg + 'a1'] = spec.a[0];
        P[leg + 'a2'] = spec.a[1];
        P[leg + 'a3'] = spec.a[2];
      } else {
        P[leg + 'p'] = 1;
        P[leg + 'x'] = spec.x;
        P[leg + 'fa'] = spec.fa;
      }
    });
    solvePose(P, solved, null);
    LEGS.forEach((leg) => {
      const r = solved[leg];
      if (P[leg + 'p'] === 1) {
        P[leg + 'a1'] = wrap(r[2] - P.cr);
        P[leg + 'a2'] = wrap(r[5] - P.cr);
        P[leg + 'a3'] = wrap(r[8] - P.cr);
      } else {
        P[leg + 'x'] = r[9];
        P[leg + 'fa'] = r[8];
        P[leg + 'y'] = 0;
      }
    });
    out[name] = P;
  });
  return out;
}

export const POSES = buildPoses();

// Size of the burrow mouth (burrow units = rig units).
export const HOLE = { rx: 23, ry: 3.5 };

// Layout of the head (head coordinates, origin at the neck pivot).
export const HEAD_GEO = {
  eye: [14.5, -12.5],
  nose: [32.4, -9],
  earNear: [4, -20.5],
  earFar: [0.5, -21.5],
  turnPivot: [12, -10]
};

// A point drawn in head coordinates, in rig units (the head is scaled about
// its turn pivot, as HeroRabbit.vue draws it).
function headPoint(R, x, y, out) {
  const pv = HEAD_GEO.turnPivot;
  const lx = pv[0] + (x - pv[0]) * HEAD_SCALE;
  const ly = pv[1] + (y - pv[1]) * HEAD_SCALE;
  const a = R.head[2] * DEG;
  out[0] = R.head[0] + lx * Math.cos(a) - ly * Math.sin(a);
  out[1] = R.head[1] + lx * Math.sin(a) + ly * Math.cos(a);
  return out;
}

// Where the tip of the nose is in a pose.
export function noseOf(P, out) {
  return headPoint(solvePose(P, newSolved(), null), HEAD_GEO.nose[0], HEAD_GEO.nose[1], out);
}

// The highest point of the ears in a pose (rig y).
const EAR_TIP = [-0.8, -36];

export function earTopOf(P) {
  const R = solvePose(P, newSolved(), null);
  const pt = [0, 0];
  let top = Infinity;
  [[HEAD_GEO.earNear, P.eN, 1], [HEAD_GEO.earFar, P.eF, 0.94]].forEach((e) => {
    const a = e[1] * DEG;
    const ex = EAR_TIP[0] * e[2];
    const ey = EAR_TIP[1] * e[2];
    headPoint(R, e[0][0] + ex * Math.cos(a) - ey * Math.sin(a), e[0][1] + ex * Math.sin(a) + ey * Math.cos(a), pt);
    top = Math.min(top, pt[1]);
  });
  return top;
}
